import { useAlertDialog } from '@fukuri-admins/ui/common/AlertDialog';
import { useToast } from '@fukuri-admins/ui/common/Toast/hooks';
import { useTooltip } from '@fukuri-admins/ui/common/Tooltip/hooks';
import { getThreeLetterCode } from '@fukuri-admins/utils/common/cookies';
import { useState } from 'react';
import { PortalListPresentation } from '../presentations';
import { useDeletePortal } from './useDeletePortal';
import { useGetPortalItems } from './useGetPortalItems';
import { usePatchClientPortalItemsReorder } from './usePatchClientPortalItemsReorder';

type Portal = {
  id: number;
  title: string;
  iconName: string;
  linkUrl?: string;
  fileUrl?: string;
  itemType: 'file' | 'url';
  sortOrder: number;
};

type PresentationProps = React.ComponentProps<typeof PortalListPresentation>;

type UsePortalList = () => {
  state: {
    data: ReturnType<typeof useGetPortalItems>['data'];
    selectedPortal: Portal | null;
    isOpenEditModal: boolean;
    isOpenCreateModal: boolean;
    tooltipId: PresentationProps['tooltipId'];
  };
  actions: {
    handleReOrder: PresentationProps['onReOrder'];
    handleShowTooltip: PresentationProps['showTooltip'];
    handleHideTooltip: PresentationProps['hideTooltip'];
    handleEditPortal: PresentationProps['handleEditPortal'];
    handleDeletePortal: PresentationProps['handleDeletePortal'];
    handleCloseEditModal: () => void;
    handleOpenCreateModal: () => void;
    handleCloseCreateModal: () => void;
    handleRefreshPortalList: () => void;
  };
};

export const usePortalList: UsePortalList = () => {
  const threeLetterCode = getThreeLetterCode();

  // APIからデータを取得
  const { data, mutate } = useGetPortalItems(threeLetterCode, {
    swr: {
      suspense: true,
      revalidateOnFocus: false,
    },
  });

  const [selectedPortal, setSelectedPortal] = useState<Portal | null>(null);
  const [isOpenEditModal, setIsOpenEditModal] = useState(false);
  const [isOpenCreateModal, setIsOpenCreateModal] = useState(false);

  const { showToast } = useToast();
  const { showAlertDialog } = useAlertDialog();
  const { showTooltip, hideTooltip, tooltipId } = useTooltip();

  const { trigger: deletePortal } = useDeletePortal(threeLetterCode, {
    onSuccess: () => {
      // キャッシュを更新
      mutate();
      showToast({
        message: 'ポータルを削除しました。',
        status: 'success',
      });
    },
    onError: () => {
      showToast({
        message: '削除に失敗しました。',
        status: 'danger',
      });
    },
  });

  const { trigger: patchPortalItemsReorder } = usePatchClientPortalItemsReorder(threeLetterCode, {
    swr: {
      onSuccess: () => {
        showToast({
          message: 'ポータルの並び順を更新しました。',
          status: 'success',
        });
      },
      onError: () => {
        showToast({
          message: '並び順の更新に失敗しました。',
          status: 'danger',
        });
      },
    },
  });

  const handleReOrder: PresentationProps['onReOrder'] = async reorderedItems => {
    const sortedIds = reorderedItems.map(item => item.id);

    // 楽観的更新：並び替え後のデータを生成
    const updatedData = {
      ...data,
      client_portal_items: reorderedItems.map((item, index) => {
        const original = data.client_portal_items.find(original => original.id === item.id);
        return {
          ...original,
          sort_order: index + 1,
        };
      }),
    };

    // SWRキャッシュを即時更新（再検証なし）
    mutate(updatedData, { revalidate: false });

    try {
      await patchPortalItemsReorder({
        sorted_client_portal_item_ids: sortedIds,
      });
    } catch {
      // エラーの場合は元のデータに戻す
      mutate();
    }
  };

  /**
   * 編集メニュー選択時のハンドラ。編集モーダルの表示に必要な値を state へ反映する。
   *
   * @param portal - 編集対象のポータル
   */
  const handleEditPortal: PresentationProps['handleEditPortal'] = portal => {
    setSelectedPortal(portal);
    setIsOpenEditModal(true);
  };

  /**
   * 削除メニュー選択時のハンドラ。AlertDialog 経由で確認後に削除 API を実行する。
   *
   * @param portal - 削除対象のポータル
   */
  const handleDeletePortal: PresentationProps['handleDeletePortal'] = portal => {
    showAlertDialog({
      title: `「${portal.title}」を削除しますか？`,
      confirmText: '削除',
      confirmButtonVariant: 'destructive',
      onConfirm: async () => {
        await deletePortal(portal.id);
      },
    });
  };

  const handleCloseEditModal = () => {
    setIsOpenEditModal(false);
    setSelectedPortal(null);
  };

  const handleOpenCreateModal = () => {
    setIsOpenCreateModal(true);
  };

  const handleCloseCreateModal = () => {
    setIsOpenCreateModal(false);
  };

  const handleRefreshPortalList = () => {
    mutate();
  };

  return {
    state: { data, selectedPortal, isOpenEditModal, isOpenCreateModal, tooltipId },
    actions: {
      handleReOrder,
      handleShowTooltip: showTooltip,
      handleHideTooltip: hideTooltip,
      handleEditPortal,
      handleDeletePortal,
      handleCloseEditModal,
      handleOpenCreateModal,
      handleCloseCreateModal,
      handleRefreshPortalList,
    },
  };
};
